import { Form, router } from "@inertiajs/react"
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  LifeBuoy,
  ShieldQuestion,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { CTAButton } from "@/components/collab-card/cta-button"
import { RewardChips } from "@/components/mission-detail/reward-chips"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { SUPPORT_MAILTO } from "@/lib/support"
import { creatorMissionsPath } from "@/routes"
import type { Collab } from "@/types/collab"
import type { Lifecycle } from "@/types/mission-detail"

/**
 * OKS-478 — §08 lifecycle view: swaps the body-above-the-CTA + the primary
 * CTA by the server-derived `lifecycle.state` (PRD §3 table). The FE NEVER
 * re-derives the state — it only renders the view the backend selected.
 *
 * CTA strategy:
 *   - **assigned** — Accept is a real POST, rendered as an Inertia `<Form>`
 *     (NOT shadcn Form / RHF / zod) to `creator_collab_transitions` with a
 *     hidden `event=accept`. On success the shared controller redirects to
 *     the missions list with a flash toast ("Mission accepted") — UX Spec §4.
 *   - **to_submit / needs_changes** — "Submit / Resubmit" hand off to the §09
 *     picker (OKS-481, not built here) → degrade to the missions list
 *     (OKS-445 precedent).
 *   - **in_review** — disabled, no action.
 *   - **approved** — reward chips + "See your rewards" (→ missions list until
 *     the rewards surface lands).
 *   - **action_needed** — Resubmit + "Contact support" (`mailto:` — never a
 *     dead `#` link).
 *   - **in_progress** — the Drop/Promotional shipping interludes fall back to
 *     the existing `collab.cta` affordance (disabled progress text).
 */
export interface LifecycleViewProps {
  lifecycle: Lifecycle
  collab: Collab
  /**
   * OKS-990 (F-MOUNT) — open the product picker INSTEAD of posting, on a drop
   * that offers a real choice.
   *
   * ⚠️ THE REQUEST HAPPENS HERE, AT `assigned`, and that is the whole reason
   * this prop exists. The first fix hung the picker off `CTAButton`'s
   * `onInterceptTap`, which renders only in `MissionDetail`'s `gifting?`
   * branch — and `isGiftingCollab` requires state ∈ {to_ship, shipped,
   * delivered}, i.e. AFTER the grant. At `assigned` this component renders
   * instead, so the picker mounted, compiled, tested green, and could not be
   * opened by any route a creator has.
   *
   * Optional because every non-drop lifecycle renders this component too. The
   * CALL SITE decides whether a choice exists (`collab.offer`); this component
   * decides only whether the affordance in hand is the one that ships a
   * product — see `intercepts` below.
   */
  onInterceptRequest?: () => void
}

/** Hand-off destination for Submit / Resubmit / See-rewards until §09 +
 *  the rewards surface ship (OKS-445 graceful-degrade precedent). */
function handoffToMissions() {
  router.visit(creatorMissionsPath())
}

export function LifecycleView({
  lifecycle,
  collab,
  onInterceptRequest,
}: LifecycleViewProps) {
  const { t } = useTranslation()
  const title = collab.mission.title

  // ⚠️ KEYED ON THE CTA KIND, not on the event. Three distinct affordances now
  // share `event === "apply"` and only ONE of them ships a chosen product:
  //
  //   request_product   — the shipping path. The creator picks. INTERCEPT.
  //   participate_owned — PRD A1's one tap. The server picks the product the
  //                       creator already holds (`no_ship_pick_product`), so a
  //                       picker here would ask a question with one answer and
  //                       contradict A1's "no dialog opens".
  //   take_part         — open / promotional. No product to choose at all.
  //
  // Intercepting on the event would have swallowed all three.
  const intercepts =
    lifecycle.state === "assigned" &&
    collab.cta.kind === "request_product" &&
    onInterceptRequest != null

  return (
    <section
      data-testid={`mission-detail-lifecycle-${lifecycle.state}`}
      data-lifecycle-state={lifecycle.state}
      className="mt-2 flex flex-col gap-3"
    >
      {/* assigned — Accept (Open/Promotional) or Apply (Drop), whichever the
          collab affords. We POST the affordance via an Inertia <Form> (NOT
          shadcn Form / RHF / zod) to the resolver-provided href + event, so a
          Drop's `apply` and an Open's `accept` both flow through here. The
          shared controller redirects to the missions list with a flash toast
          on success (UX Spec §4). */}
      {/* ⚠️ The intercept is checked BEFORE the Form branch, and it renders a
          plain button rather than a submit inside the Form. A `type="button"`
          nested in the Inertia `<Form>` would still be a child of a form whose
          action posts WITHOUT a product — one stray Enter keypress in that
          subtree submits it, and the server answers `product_required`, which
          the creator sees as a refusal they cannot act on. Keeping it outside
          the Form means the un-picked post is not reachable at all. */}
      {intercepts ? (
        <Button
          type="button"
          onClick={onInterceptRequest}
          data-testid="mission-detail-request-picker-trigger"
          data-cta-kind={collab.cta.kind}
          aria-label={t(`creator_home.cta.aria.${collab.cta.kind}`, {
            title,
            defaultValue: t("creator_home.cta.aria.apply", { title }),
          })}
          className="h-11 w-full rounded-full text-sm font-semibold"
        >
          <CheckCircle2 aria-hidden="true" />
          {t(collab.cta.label_key)}
        </Button>
      ) : lifecycle.state === "assigned" && collab.cta.href ? (
        <Form
          action={collab.cta.href}
          method="post"
          options={{ preserveScroll: true }}
        >
          {({ processing }) => (
            <>
              <input
                type="hidden"
                name="event"
                value={collab.cta.event ?? "accept"}
              />
              {/* ⚠️ OKS-988 F1 — the ship / don't-ship boolean, and it MUST be
                  transmitted explicitly on this path.

                  Decision 3 is "both paths fire the same `apply` event with one
                  boolean". This Form previously posted only `event`, so the
                  boolean never left the browser — and its absence is NOT
                  inert: `ActiveModel::Type::Boolean.cast(nil)` is falsy, so a
                  missing field reads as "ship it", which is the destructive
                  direction. A creator who tapped "I'll use the one I have" got
                  a parcel anyway and had the brand's batch pre-selection
                  cleared underneath them (AC5 + AC9).

                  It also failed differently depending on the creator's TTL
                  state, from the same button: a flagged creator's request was
                  routed into the re-request branch, which demands a written
                  reason, so the primary silently refused instead of granting.
                  One missing field, two unrelated-looking symptoms.

                  Sent only for `apply` — the open-mission `accept` path has no
                  such choice and the server never reads it there. */}
              {collab.cta.event === "apply" && (
                <input
                  type="hidden"
                  name="no_ship"
                  value={String(collab.cta.kind === "participate_owned")}
                  data-testid="mission-detail-no-ship-field"
                />
              )}
              <Button
                type="submit"
                disabled={processing}
                data-testid="mission-detail-accept-submit"
                data-cta-event={collab.cta.event ?? "accept"}
                /* ⚠️ OKS-988 — keyed on the CTA KIND, falling back to the
                   event. The event is the state-machine verb and several
                   distinct affordances now share `apply`
                   (`request_product` / `participate_owned` / `take_part`), so
                   an event-keyed aria would announce "Take part in X" to a
                   screen-reader user looking at "I'll use the one I have".
                   PRD §3b specifies a distinct aria per affordance and UX §6
                   requires the announcement to follow the visible label. */
                aria-label={t(`creator_home.cta.aria.${collab.cta.kind}`, {
                  title,
                  defaultValue: t(
                    `creator_home.cta.aria.${collab.cta.event ?? "accept"}`,
                    { title },
                  ),
                })}
                className="h-11 w-full rounded-full text-sm font-semibold"
              >
                <CheckCircle2 aria-hidden="true" />
                {processing
                  ? t("creator_mission_detail.lifecycle.assigned.accepting")
                  : t(collab.cta.label_key)}
              </Button>
            </>
          )}
        </Form>
      ) : lifecycle.state === "assigned" ? (
        // Defensive: an assigned collab with no POST href falls back to the
        // shared affordance rather than rendering a dead button.
        <CTAButton cta={collab.cta} missionTitle={title} />
      ) : null}

      {lifecycle.state === "to_submit" && (
        <>
          <Guidance
            icon={Clock}
            body={t("creator_mission_detail.lifecycle.to_submit.guidance")}
          />
          <Button
            type="button"
            onClick={handoffToMissions}
            data-testid="mission-detail-submit-cta"
            className="h-11 w-full rounded-full text-sm font-semibold"
          >
            {t("creator_mission_detail.lifecycle.to_submit.cta")}
          </Button>
        </>
      )}

      {lifecycle.state === "in_review" && (
        <Button
          type="button"
          variant="outline"
          disabled
          data-testid="mission-detail-in-review"
          className="h-11 w-full rounded-full text-sm font-semibold"
        >
          <Clock aria-hidden="true" />
          {t("creator_mission_detail.lifecycle.in_review.cta")}
        </Button>
      )}

      {lifecycle.state === "approved" && lifecycle.rewards && (
        <>
          <Badge
            className="bg-primary/10 text-primary w-fit gap-1"
            data-testid="mission-detail-approved-badge"
          >
            <CheckCircle2 className="size-3.5" aria-hidden="true" />
            {t("creator_mission_detail.lifecycle.approved.badge")}
          </Badge>
          <p className="text-sm font-semibold">
            {t("creator_mission_detail.lifecycle.approved.headline")}
          </p>
          <p className="text-muted-foreground text-sm">
            {t("creator_mission_detail.lifecycle.approved.subhead", {
              brand: collab.brand.name,
            })}
          </p>
          <RewardChips rewards={lifecycle.rewards} />
          <Button
            type="button"
            onClick={handoffToMissions}
            data-testid="mission-detail-see-rewards"
            className="h-11 w-full rounded-full text-sm font-semibold"
          >
            {t("creator_mission_detail.lifecycle.approved.cta")}
          </Button>
        </>
      )}

      {lifecycle.state === "needs_changes" && (
        <>
          <BrandNoteCard
            badgeKey="creator_mission_detail.lifecycle.needs_changes.badge"
            note={lifecycle.note}
            source={lifecycle.note_source}
          />
          <Button
            type="button"
            onClick={handoffToMissions}
            data-testid="mission-detail-resubmit-cta"
            className="h-11 w-full rounded-full text-sm font-semibold"
          >
            {t("creator_mission_detail.lifecycle.needs_changes.cta")}
          </Button>
        </>
      )}

      {lifecycle.state === "action_needed" && (
        <>
          <BrandNoteCard
            badgeKey="creator_mission_detail.lifecycle.action_needed.badge"
            note={lifecycle.note}
            source={lifecycle.note_source}
          />
          <Button
            type="button"
            onClick={handoffToMissions}
            data-testid="mission-detail-resubmit-cta"
            className="h-11 w-full rounded-full text-sm font-semibold"
          >
            {t("creator_mission_detail.lifecycle.action_needed.cta")}
          </Button>
          <Button
            asChild
            variant="outline"
            data-testid="mission-detail-contact-support"
            className="h-11 w-full rounded-full text-sm font-semibold"
          >
            <a href={SUPPORT_MAILTO}>
              <LifeBuoy aria-hidden="true" />
              {t("creator_mission_detail.lifecycle.action_needed.support")}
            </a>
          </Button>
        </>
      )}

      {lifecycle.state === "in_progress" && (
        <CTAButton
          cta={collab.cta}
          missionTitle={title}
          onSubmitTap={handoffToMissions}
        />
      )}

      {/* OKS-483 — expired (terminal): loss-aversion "went unclaimed" copy +
          Request reopen CTA (request_reopen POST via the shared CTAButton). */}
      {lifecycle.state === "expired" && (
        <>
          <Badge
            variant="secondary"
            className="w-fit gap-1"
            data-testid="mission-detail-expired-badge"
          >
            <Clock className="size-3.5" aria-hidden="true" />
            {t("creator_mission_detail.lifecycle.expired.badge")}
          </Badge>
          <p className="text-sm font-semibold">
            {t("creator_mission_detail.lifecycle.expired.headline")}
          </p>
          <p className="text-muted-foreground text-sm">
            {t("creator_mission_detail.lifecycle.expired.body", {
              xp: collab.mission.reward_xp,
              coins: collab.mission.reward_coins,
              brand: collab.brand.name,
            })}
          </p>
          <CTAButton cta={collab.cta} missionTitle={title} />
        </>
      )}

      {/* OKS-483 — reopen_requested (interim): "waiting on {brand}" copy +
          disabled affordance (the CTAButton renders reopen_requested disabled). */}
      {lifecycle.state === "reopen_requested" && (
        <>
          <Badge
            className="w-fit gap-1 bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100"
            data-testid="mission-detail-reopen-requested-badge"
          >
            <ShieldQuestion className="size-3.5" aria-hidden="true" />
            {t("creator_mission_detail.lifecycle.reopen_requested.badge")}
          </Badge>
          <p className="text-sm font-semibold">
            {t("creator_mission_detail.lifecycle.reopen_requested.headline")}
          </p>
          <p className="text-muted-foreground text-sm">
            {t("creator_mission_detail.lifecycle.reopen_requested.body", {
              brand: collab.brand.name,
            })}
          </p>
          {/* Disabled affordance with the brand name interpolated (the shared
              CTAButton can't interpolate, so the detail sheet renders its own
              brand-named "Waiting on {brand}" — UX A3). */}
          <Button
            type="button"
            variant="outline"
            disabled
            aria-disabled="true"
            data-testid="mission-detail-reopen-waiting"
            className="h-11 w-full rounded-full text-sm font-semibold"
          >
            <Clock aria-hidden="true" />
            {t("creator_mission_detail.lifecycle.reopen_requested.cta", {
              brand: collab.brand.name,
            })}
          </Button>
        </>
      )}
    </section>
  )
}

/** Quoted brand-note card — shared by needs_changes + action_needed. The note
 *  is the brand's verbatim text; it scrolls within the card when long
 *  (UX Spec §3 edge). State carries text + icon, never color alone (§6). */
function BrandNoteCard({
  badgeKey,
  note,
  source,
}: {
  badgeKey: string
  note: string | null
  source: string | null
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2">
      <Badge
        variant="destructive"
        className="w-fit gap-1"
        data-testid="mission-detail-lifecycle-badge"
      >
        <AlertTriangle className="size-3.5" aria-hidden="true" />
        {t(badgeKey)}
      </Badge>
      {note && (
        <figure className="border-destructive/30 bg-destructive/5 max-h-40 overflow-y-auto rounded-lg border p-3">
          <blockquote
            className="text-sm whitespace-pre-wrap"
            data-testid="mission-detail-note"
          >
            {note}
          </blockquote>
          {source && (
            <figcaption className="text-muted-foreground mt-2 text-xs font-medium">
              {t("creator_mission_detail.lifecycle.note_attribution", {
                brand: source,
              })}
            </figcaption>
          )}
        </figure>
      )}
    </div>
  )
}

/** Inline guidance line (icon + body) for the to-submit view. */
function Guidance({ icon: Icon, body }: { icon: typeof Clock; body: string }) {
  return (
    <p
      className="text-muted-foreground flex items-start gap-2 text-sm"
      data-testid="mission-detail-guidance"
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>{body}</span>
    </p>
  )
}
